// for svg import - see https://stackoverflow.com/questions/70309561/unable-to-import-svg-with-vite-as-reactcomponent
/// <reference types="vite-plugin-svgr/client" />

import React, { use, useCallback, useEffect } from 'react';
import { NavigateFunction, useInRouterContext, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import SmallBackArrowIcon from '../../assets/arrow-previous-left-icon.svg?react';
import { isInsideAppleAppStoreApp } from '../../util/billing.ts';
import { goBack } from '../../util/go_back.ts';
import { getPlatform, Platform } from '../../util/platform.ts';
import { AppContext, AppContextInterface, PageAnimation } from './app_context.tsx';

const maxHeight = '1000px';
const maxWidth = '500px';

interface Props {
  // The onboarding page this frame shows.  While the location shows it, the frame hands its back and close handlers
  // (and suppressBackButton) to onboarding's chrome; undefined for a page outside onboarding's hash routes (the
  // invite pages), which hands them over while no onboarding page shows.
  onboardingPage: OnboardingPages | undefined;
  // Content shown over a host page whose own frame owns those handlers (EditUser over Choose User, or inside a
  // dialog), so it never hands its own over
  nested?: boolean;
  mainContent: React.ReactElement;
  footerContent?: React.ReactElement;
  suppressBackButton?: boolean;
  onEnterKeypress?: () => void;
  containsInputElement?: boolean;
  containsMultipleButtons?: boolean;
  isSmallBackButton?: boolean;
  onBack?: () => void;
  alignTop?: boolean;
  onClose?: () => void;
  embed?: boolean;
  header?: React.ReactElement;
  allowRotation?: boolean;
}

// Frame also renders inside react-confirm dialogs (add_custom_song.tsx), which mount in a React root of
// their own outside the app's router, where useNavigate() throws (crash reports #75270-#75276).  Those
// dialogs are embedded frames without the small back button, so they have no use for navigate anyway.
export const Frame = (props: Props) =>
  useInRouterContext() ? <RoutedFrame {...props} /> : <FrameContent {...props} />;

const RoutedFrame = (props: Props) => {
  const navigate = useNavigate();
  return <FrameContent {...props} navigate={navigate} />;
};

const FrameContent = ({
  onboardingPage,
  nested,
  mainContent,
  footerContent,
  suppressBackButton,
  onEnterKeypress,
  containsInputElement,
  containsMultipleButtons,
  isSmallBackButton,
  onBack,
  alignTop,
  onClose,
  embed,
  header,
  allowRotation,
  navigate,
}: Props & { navigate?: NavigateFunction }) => {
  const appContext = use(AppContext);

  const onKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.code === 'Enter') {
        onEnterKeypress?.();
        evt.stopPropagation();
        evt.preventDefault();
      }
    },
    [onEnterKeypress],
  );

  const locationMatchesCurrentPage = useCallback(() => {
    if (nested) {
      return false;
    }
    const { hash } = window.location;
    const hashPage = hash && hash.slice(1);
    const hasNoOnboardingPage = !onboardingPage || onboardingPage === OnboardingPages.Demo;
    const allOnboardingPages = Object.values(OnboardingPages) as string[];
    if (hashPage === onboardingPage) {
      return true;
    }
    return hasNoOnboardingPage && (!hashPage || !allOnboardingPages.includes(hashPage));
  }, [nested, onboardingPage]);

  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener('keydown', onKeyDown, { signal: controller.signal });
    return () => controller.abort();
  }, [onKeyDown]);

  useEffect(() => {
    if (locationMatchesCurrentPage()) {
      appContext?.setSuppressBackButton(Boolean(suppressBackButton));
      // when passing a function to a setter, it assumes you want to invoke it to store the result -
      // like when you are initializing a state variable (see https://stackoverflow.com/a/68019979/998490)
      appContext?.setOnBack(() => onBack);
      appContext?.setOnClose(() => onClose);
    }
  }, [appContext, locationMatchesCurrentPage, onboardingPage, onBack, onClose, suppressBackButton]);

  const onBackCallback = useCallback(() => {
    navigateBack(appContext, navigate, onBack);
  }, [appContext, navigate, onBack]);

  // The Android mobile keyboard contains a "Go" button intended to submit a <form>.
  // Including a <form> in the page causes the browser to reload server content on back & submit.
  // Thus, we squeeze the page vertically for Android so that the Continue button is always visible.
  // If we similarly squeeze the page on iOS, the page gets scrolled off the top by the keyboard.  :/
  const squeezePageHeight = containsInputElement && getPlatform() === Platform.Mobile;

  const isFirefoxBrowser = window.navigator.userAgent.includes('Firefox');

  const innerWrapper = (
    <InnerWrapper
      $squeezePageHeight={squeezePageHeight}
      $containsMultipleButtons={containsMultipleButtons}
      $allowRotation={allowRotation}
      $embed={embed}
    >
      {isSmallBackButton && (
        <BackButton key="back" aria-label="Back" onClick={onBackCallback}>
          <SmallBackArrow />
        </BackButton>
      )}
      <HeaderWrapper>{header}</HeaderWrapper>
      <MainWrapper $alignTop={alignTop} $clearBackButton={isSmallBackButton && !alignTop}>
        {mainContent}
      </MainWrapper>
      {footerContent && (
        <FooterWrapper $squeezePageHeight={squeezePageHeight} $embed={embed} $topFade={!isFirefoxBrowser}>
          {footerContent}
        </FooterWrapper>
      )}
    </InnerWrapper>
  );

  if (embed) {
    return innerWrapper;
  }

  return <OuterWrapper $squeezePageHeight={squeezePageHeight}>{innerWrapper}</OuterWrapper>;
};

// navigate is undefined only outside the router (see Frame), where the browser's own history is all there is
export function navigateBack(
  appContext: AppContextInterface | null,
  navigate: NavigateFunction | undefined,
  onBack?: () => void,
) {
  appContext?.setPageAnimation(PageAnimation.LeftToRight);
  if (onBack) {
    onBack();
  } else if (navigate) {
    void goBack(navigate);
  } else {
    window.history.back();
  }
}

export const BackButton = styled.button`
  z-index: 1; // TODO(hewitt): should not be necessary - wasn't when in frame.ts
  position: absolute;
  top: 15px;
  left: 5px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  fill: var(--color-text);
`;

// Holds a top-aligned page's prompt in the small back button's row: vertically centred on the arrow and inset by the
// arrow's width on both sides, so a wide prompt wraps beside the arrow instead of running underneath it (#3513)
// or being pushed below it, which left the search box under the soft keyboard on small phones (#3890)
export const BesideSmallBackButton = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  // the arrow glyph is 20px tall below the button's 15px top and 8px padding, so this centres the row on it at 33px
  margin-top: 13px;
  min-height: 40px;
  // the button ends 28px from the left (5px left + 10px padding + the glyph), plus a gap
  padding: 0 40px;
  text-align: center;
`;

const OuterWrapper = styled.div<{ $squeezePageHeight: boolean | undefined }>`
  display: grid;
  place-items: ${(props) => (props.$squeezePageHeight ? 'start' : 'center')};
  width: 100vw;
  height: ${isInsideAppleAppStoreApp() ? '100vh' : 'var(--view-height, 100vh)'};
  background-color: var(--color-background);
`;

const InnerWrapper = styled.div<{
  $squeezePageHeight?: boolean;
  $containsMultipleButtons?: boolean;
  $embed?: boolean;
  $allowRotation?: boolean;
}>`
  display: flex;
  position: relative;
  flex-direction: column;
  font-size: min(max(min(4.5vw, 3vh), 1rem), 1.5rem);

  // Account for mobile nav bar height (iOS Safari fails to access the CSS variable, so short circuit)
  height: min(
    ${maxHeight},
    ${(props) =>
      props.$squeezePageHeight || props.$embed
        ? '100%'
        : isInsideAppleAppStoreApp()
          ? '100vh'
          : 'var(--view-height, 100vh)'}
  );
  width: ${(props) => (props.$embed ? '100%' : `min(${maxWidth}, 100%)`)};

  // TODO(hewitt): Prevent screen rotation during onboarding
  //--screen-height-not-rotated: max(100vw, var(--view-height, 100vh));
  //--screen-width-not-rotated: min(100vw, var(--view-height, 100vh));
  //height: var(--screen-height-not-rotated);
  //width: var(--screen-width-not-rotated);
  //@media (orientation: landscape) {
  //  transform-origin: calc(100vw / 2) calc(100vh / 2);
  //  // transform-origin: calc(100vh / 2) calc(100vw / 2);
  //  transform: rotate(-90deg);
  //}
  // tried placing the following in the page header
  // <meta http-equiv="ScreenOrientation" content="autoRotate:disabled">
`;

const SmallBackArrow = styled(SmallBackArrowIcon)`
  color: var(--color-text-light);
  height: 28px;
  padding-left: 10px;
  padding-top: 8px;
`;

const HeaderWrapper = styled.div`
  flex-shrink: 0;
`;

const MainWrapper = styled.div<{ $alignTop?: boolean; $clearBackButton?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.$alignTop ? 'start' : 'center')};
  // the small back button is absolutely positioned in the top left corner (top 15px + 8px padding + 28px icon),
  // so keep vertically centred content that grows to the top below it rather than letting a wide prompt run
  // underneath the arrow; top-aligned pages put their prompt beside the arrow instead (BesideSmallBackButton)
  padding-top: ${(props) => (props.$clearBackButton ? '35px' : '0')};
`;

const FooterWrapper = styled.div<{ $squeezePageHeight?: boolean; $embed?: boolean; $topFade: boolean }>`
  flex-shrink: 0;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: ${(props) => (props.$squeezePageHeight ? '-50px' : '0')};
  padding-top: ${(props) => (props.$embed ? '10px' : '25px')};
  padding-bottom: ${(props) => (props.$embed ? '20px' : '30px')}; 
  background-color: var(--color-background);

  // fade out the top edge of the footer (doesn't work for firefox - actually, broken by condition)
  {$props => props.$topFade ?
    '-webkit-mask-image: -webkit-gradient(linear, left top, left 5%, from(rgba(0,0,0,0)), to(rgba(0,0,0,1)))' : ''
  };
`;
